import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SidebarNavigation from '../../components/ui/SidebarNavigation';
import NavigationToggle from '../../components/ui/NavigationToggle';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import UserProfileHeader from '../../components/ui/UserProfileHeader';
import RequestsTable from './components/RequestsTable';
import RequestsFilters from './components/RequestsFilters';
import RequestsMobileCard from './components/RequestsMobileCard';
import RequestsPagination from './components/RequestsPagination';
import BulkActions from './components/BulkActions';

const RequestsManagement = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCaseType, setSelectedCaseType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const itemsPerPage = 10;

  // Mock data for requests
  const mockRequests = [
    {
      id: 1,
      caseType: 'اظهارنامه قضایی',
      subject: 'اظهارنامه دفاعیه در پرونده کلاهبرداری',
      claimant: 'احمد محمدی',
      respondent: 'شرکت تجاری پارس',
      status: 'تکمیل شده',
      createdAt: '۱۴۰۳/۰۸/۱۲',
      description:
        'تهیه اظهارنامه دفاعیه در خصوص اتهام کلاهبرداری و ارائه مدارک مثبته',
    },
    {
      id: 2,
      caseType: 'شکایت',
      subject: 'شکایت از نقض قرارداد خرید و فروش',
      claimant: 'مریم احمدی',
      respondent: 'علی رضایی',
      status: 'در حال بررسی',
      createdAt: '۱۴۰۳/۰۸/۱۰',
      description:
        'شکایت از نقض شرایط قرارداد خرید و فروش ملک و عدم انتقال سند',
    },
    {
      id: 3,
      caseType: 'لایحه',
      subject: 'لایحه تجدیدنظرخواهی حکم دادگاه',
      claimant: 'حسن کریمی',
      respondent: 'بانک ملی ایران',
      status: 'نیاز به بازنگری',
      createdAt: '۱۴۰۳/۰۸/۰۸',
      description:
        'تهیه لایحه تجدیدنظرخواهی از حکم دادگاه در خصوص پرونده مطالبه وجه',
    },
    {
      id: 4,
      caseType: 'اظهارنامه قضایی',
      subject: 'اظهارنامه در پرونده طلاق توافقی',
      claimant: 'فاطمه نوری',
      respondent: 'محمد نوری',
      status: 'تکمیل شده',
      createdAt: '۱۴۰۳/۰۸/۰۵',
      description: 'تهیه اظهارنامه موافقت با طلاق و تعیین حقوق فرزندان',
    },
    {
      id: 5,
      caseType: 'شکایت',
      subject: 'شکایت از تصادف رانندگی',
      claimant: 'رضا محمودی',
      respondent: 'بیمه ایران',
      status: 'در حال بررسی',
      createdAt: '۱۴۰۳/۰۸/۰۳',
      description: 'شکایت از عدم پرداخت خسارت تصادف و مطالبه غرامت',
    },
    {
      id: 6,
      caseType: 'لایحه',
      subject: 'لایحه اعتراض به رأی هیئت حل اختلاف',
      claimant: 'شرکت ساختمانی آریا',
      respondent: 'شهرداری تهران',
      status: 'تکمیل شده',
      createdAt: '۱۴۰۳/۰۸/۰۱',
      description: 'اعتراض به رأی هیئت حل اختلاف در خصوص پرونده مطالبه مطالبات',
    },
  ];

  // Filter and sort requests
  const filteredAndSortedRequests = useMemo(() => {
    let filtered = mockRequests?.filter((request) => {
      const matchesSearch =
        !searchQuery ||
        request?.subject?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        request?.description
          ?.toLowerCase()
          ?.includes(searchQuery?.toLowerCase());

      const matchesCaseType =
        !selectedCaseType || request?.caseType === selectedCaseType;
      const matchesStatus =
        !selectedStatus || request?.status === selectedStatus;

      // Simple date filtering (in real app, would use proper date parsing)
      const matchesDateRange = (!dateRange?.from && !dateRange?.to) || true;

      return (
        matchesSearch && matchesCaseType && matchesStatus && matchesDateRange
      );
    });

    // Sort requests
    filtered?.sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];

      if (sortField === 'createdAt') {
        // Simple date comparison (in real app, would use proper date parsing)
        aValue = a?.createdAt;
        bValue = b?.createdAt;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [
    mockRequests,
    searchQuery,
    selectedCaseType,
    selectedStatus,
    dateRange,
    sortField,
    sortDirection,
  ]);

  // Pagination
  const totalPages = Math.ceil(
    filteredAndSortedRequests?.length / itemsPerPage
  );
  const paginatedRequests = filteredAndSortedRequests?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCaseType, selectedStatus, dateRange]);

  const handleSelectRequest = (requestId, checked) => {
    if (checked) {
      setSelectedRequests((prev) => [...prev, requestId]);
    } else {
      setSelectedRequests((prev) => prev?.filter((id) => id !== requestId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRequests(paginatedRequests?.map((request) => request?.id));
    } else {
      setSelectedRequests([]);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewRequest = (requestId) => {
    // Navigate to request details page
    console.log('Viewing request:', requestId);
  };

  const handleEditRequest = (requestId) => {
    // Navigate to edit request page
    console.log('Editing request:', requestId);
  };

  const handleDownloadRequest = (requestId) => {
    const request = mockRequests?.find((r) => r?.id === requestId);
    if (request) {
      const content = `نوع پرونده: ${request?.caseType}\nموضوع: ${request?.subject}\nشاکی: ${request?.claimant}\nمشکو علیه: ${request?.respondent}\nتوضیحات: ${request?.description}`;
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `درخواست-${requestId}.txt`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCaseType('');
    setSelectedStatus('');
    setDateRange({ from: '', to: '' });
  };

  const handleBulkDelete = () => {
    console.log('Bulk deleting requests:', selectedRequests);
    setSelectedRequests([]);
  };

  const handleBulkExport = () => {
    console.log('Bulk exporting requests:', selectedRequests);
  };

  const handleBulkStatusChange = (newStatus) => {
    console.log(
      'Changing status to:',
      newStatus,
      'for requests:',
      selectedRequests
    );
    setSelectedRequests([]);
  };

  const handleClearSelection = () => {
    setSelectedRequests([]);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Toggle for Mobile */}
      <NavigationToggle
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      {/* Sidebar */}
      <SidebarNavigation
        isCollapsed={!sidebarOpen}
        onToggle={() => setSidebarOpen(false)}
      />
      {/* Main Content */}
      <div className="lg:mr-80 min-h-screen">
        {/* Top Bar */}
        <header className="bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                مدیریت درخواست‌ها
              </h1>
              <p className="text-muted-foreground mt-1">
                مشاهده و مدیریت تمام درخواست‌های حقوقی شما
              </p>
            </div>

            <div className="flex items-center space-x-4 space-x-reverse">
              <Button
                onClick={() => navigate('/new-request-form')}
                className="flex items-center space-x-2 space-x-reverse">
                <Icon name="Plus" size={20} />
                <span>درخواست جدید</span>
              </Button>

              <UserProfileHeader onLogout={handleLogout} />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <BreadcrumbTrail />

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    کل درخواست‌ها
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {mockRequests?.length?.toLocaleString('fa-IR')}
                  </p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    در حال بررسی
                  </p>
                  <p className="text-2xl font-bold text-warning">
                    {mockRequests
                      ?.filter((r) => r?.status === 'در حال بررسی')
                      ?.length?.toLocaleString('fa-IR')}
                  </p>
                </div>
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={24} className="text-warning" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    تکمیل شده
                  </p>
                  <p className="text-2xl font-bold text-success">
                    {mockRequests
                      ?.filter((r) => r?.status === 'تکمیل شده')
                      ?.length?.toLocaleString('fa-IR')}
                  </p>
                </div>
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    نیاز به بازنگری
                  </p>
                  <p className="text-2xl font-bold text-destructive">
                    {mockRequests
                      ?.filter((r) => r?.status === 'نیاز به بازنگری')
                      ?.length?.toLocaleString('fa-IR')}
                  </p>
                </div>
                <div className="w-12 h-12 bg-destructive/10 rounded-lg flex items-center justify-center">
                  <Icon
                    name="AlertCircle"
                    size={24}
                    className="text-destructive"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <RequestsFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCaseType={selectedCaseType}
            onCaseTypeChange={setSelectedCaseType}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onClearFilters={handleClearFilters}
          />

          {/* Bulk Actions */}
          <BulkActions
            selectedCount={selectedRequests?.length}
            onBulkDelete={handleBulkDelete}
            onBulkExport={handleBulkExport}
            onBulkStatusChange={handleBulkStatusChange}
            onClearSelection={handleClearSelection}
          />

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3 space-x-reverse">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="text-muted-foreground">
                  در حال بارگذاری...
                </span>
              </div>
            </div>
          )}

          {/* Requests Table/Cards */}
          {!loading && (
            <>
              {isMobile ? (
                <RequestsMobileCard
                  requests={paginatedRequests}
                  selectedRequests={selectedRequests}
                  onSelectRequest={handleSelectRequest}
                  onViewRequest={handleViewRequest}
                  onEditRequest={handleEditRequest}
                  onDownloadRequest={handleDownloadRequest}
                />
              ) : (
                <RequestsTable
                  requests={paginatedRequests}
                  selectedRequests={selectedRequests}
                  onSelectRequest={handleSelectRequest}
                  onSelectAll={handleSelectAll}
                  onSort={handleSort}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onViewRequest={handleViewRequest}
                  onEditRequest={handleEditRequest}
                  onDownloadRequest={handleDownloadRequest}
                />
              )}

              {/* Pagination */}
              <RequestsPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredAndSortedRequests?.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </>
          )}

          {/* Empty State */}
          {!loading && filteredAndSortedRequests?.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon
                  name="FileText"
                  size={32}
                  className="text-muted-foreground"
                />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                هیچ درخواستی یافت نشد
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || selectedCaseType || selectedStatus
                  ? 'با فیلترهای انتخابی هیچ درخواستی پیدا نشد. لطفاً فیلترها را تغییر دهید.'
                  : 'هنوز هیچ درخواستی ثبت نکرده‌اید. اولین درخواست خود را ایجاد کنید.'}
              </p>
              <Button
                onClick={() => navigate('/new-request-form')}
                className="flex items-center space-x-2 space-x-reverse mx-auto">
                <Icon name="Plus" size={20} />
                <span>ایجاد درخواست جدید</span>
              </Button>
            </div>
          )}
        </main>
      </div>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default RequestsManagement;
